import {
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import WorkspacesOutlinedIcon from "@mui/icons-material/WorkspacesOutlined";

import colors from "../../Constants/colors";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { useWorkspaces } from "../../Query/useWorkspace";
import { useDeleteWorkspace } from "../../Query/useWorkspace";
import { Workspace } from "../../Models/workspace";
import { enqueueSnackbar } from "notistack";
import ConfirmationDialog from "./ConfiramtionDialog";
import { useTranslation } from "react-i18next";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";

const ViewWorkspaces = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("workspaces");
  const { colors: workspace } = useWorkspaceColors();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState<Workspace | null>(
    null,
  );
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;

  const { data: workspaceData } = useWorkspaces(page, rowsPerPage);

  const parseNamespaces = (namespaces: any): string[] => {
    if (Array.isArray(namespaces)) {
      return namespaces;
    }
    if (typeof namespaces === "string") {
      try {
        return JSON.parse(namespaces.replace(/'/g, '"'));
      } catch {
        enqueueSnackbar("Failed to parse namespaces.", { variant: "error" });
      }
    }
    return [];
  };

  const handleEdit = (ws: Workspace) => {
    const parsedNamespaces = parseNamespaces(ws.namespaces);
    navigate("/workspace/create", {
      state: {
        workspace: {
          ...ws,
          namespaces: parsedNamespaces,
        },
      },
    });
  };

  const handleDeleteClick = (ws: Workspace) => {
    setSelectedWorkspace(ws);
    setOpenDialog(true);
  };

  const { mutate: deleteWorkspaceMutation } = useDeleteWorkspace();

  const handleDeleteConfirm = async (workspaceName: string) => {
    if (selectedWorkspace && selectedWorkspace.name === workspaceName) {
      deleteWorkspaceMutation(selectedWorkspace.id, {
        onSuccess: () => setOpenDialog(false),
        onError: () => setOpenDialog(false),
      });
    }
  };

  const handleDeleteCancel = () => {
    setOpenDialog(false);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage + 1);
  };

  const headerCellSx = {
    fontWeight: 600,
    color: colors.gray700,
    backgroundColor: colors.gray50,
    borderBottom: `1px solid ${colors.gray200}`,
    fontSize: "0.8rem",
  };

  const bodyCellSx = {
    color: colors.primary_dark,
    fontSize: "0.875rem",
    borderBottom: `1px solid ${colors.gray100}`,
  };

  return (
    <Box
      sx={{ minHeight: "calc(100vh - 64px)", backgroundColor: colors.gray50 }}
    >
      <Box sx={{ maxWidth: "1280px", mx: "auto", px: { xs: 2, sm: 3 }, py: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 2,
            border: `1px solid ${colors.gray200}`,
            borderLeft: `4px solid ${workspace.primary_dark}`,
            background: colors.white,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            spacing={2}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <WorkspacesOutlinedIcon
                sx={{ fontSize: 32, color: workspace.primary_dark }}
              />
              <Box>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 600, color: workspace.primary_dark }}
                >
                  {t("workspaces")}
                </Typography>
                <Typography variant="caption" sx={{ color: colors.gray500 }}>
                  {workspaceData?.total ?? 0}
                </Typography>
              </Box>
            </Stack>

            <IconButton
              onClick={() =>
                navigate("/workspace/create", {
                  state: { workspace: null },
                })
              }
              sx={{
                bgcolor: workspace.accent,
                color: workspace.primary_dark,
                borderRadius: 1.5,
                px: 1.5,
                py: 1,
                gap: 0.75,
                "&:hover": { bgcolor: workspace.accent, opacity: 0.9 },
              }}
            >
              <AddCircleOutlineIcon sx={{ fontSize: 20 }} />
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, fontSize: "0.85rem" }}
              >
                {t("createNewWorkspace")}
              </Typography>
            </IconButton>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            border: `1px solid ${colors.gray200}`,
            borderLeft: `4px solid ${workspace.primary_dark}`,
            background: colors.white,
          }}
        >
          <TableContainer
            sx={{
              border: `1px solid ${colors.gray200}`,
              borderRadius: 2,
              overflow: "auto",
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headerCellSx, width: "40%" }}>
                    {t("name")}
                  </TableCell>
                  <TableCell sx={{ ...headerCellSx, width: "40%" }}>
                    {t("namespace")}
                  </TableCell>
                  <TableCell
                    sx={{ ...headerCellSx, width: "20%" }}
                    align="right"
                  >
                    {t("actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(workspaceData?.workspaces ?? []).length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      align="center"
                      sx={{
                        color: colors.gray500,
                        py: 6,
                        backgroundColor: colors.gray50,
                        fontSize: "0.875rem",
                      }}
                    >
                      {t("noWorkspaces")}
                    </TableCell>
                  </TableRow>
                ) : (
                  (workspaceData?.workspaces || []).map((ws) => {
                    const ns = parseNamespaces(ws.namespaces);
                    return (
                      <TableRow
                        key={ws.id}
                        sx={{
                          cursor: "pointer",
                          transition: "background-color 0.15s ease-in-out",
                          "&:hover": {
                            backgroundColor: `${workspace.primary_dark}08`,
                          },
                        }}
                      >
                        <TableCell
                          sx={bodyCellSx}
                          onClick={() => navigate(`/workspace/${ws.id}`)}
                        >
                          {ws.name}
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          {ns.length > 0 ? (
                            <Stack direction="row" flexWrap="wrap" gap={0.5}>
                              {ns.slice(0, 3).map((n) => (
                                <Chip
                                  key={n}
                                  label={n}
                                  size="small"
                                  sx={{
                                    bgcolor: colors.blue50,
                                    color: colors.blue700,
                                    border: `1px solid ${colors.blue200}`,
                                    fontSize: "0.7rem",
                                    height: 20,
                                  }}
                                />
                              ))}
                              {ns.length > 3 && (
                                <Chip
                                  label={`+${ns.length - 3}`}
                                  size="small"
                                  sx={{
                                    bgcolor: colors.gray100,
                                    color: colors.gray700,
                                    fontSize: "0.7rem",
                                    height: 20,
                                  }}
                                />
                              )}
                            </Stack>
                          ) : (
                            <Typography
                              variant="caption"
                              sx={{
                                color: colors.gray400,
                                fontStyle: "italic",
                              }}
                            >
                              N/A
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={bodyCellSx} align="right">
                          <Tooltip title={t("edit", { defaultValue: "Edit" })}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(ws);
                              }}
                              sx={{ color: workspace.accent }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            title={t("delete", { defaultValue: "Delete" })}
                          >
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(ws);
                              }}
                              sx={{ color: colors.red600 }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
            <TablePagination
              rowsPerPageOptions={[]}
              component="div"
              count={workspaceData?.total || 0}
              rowsPerPage={rowsPerPage}
              page={page - 1}
              onPageChange={handleChangePage}
              labelRowsPerPage=""
              sx={{
                "& .MuiTablePagination-displayedRows": {
                  color: colors.gray600,
                  fontSize: "0.8rem",
                },
                "& .MuiIconButton-root": {
                  color: workspace.primary_dark,
                },
              }}
            />
          </Box>
        </Paper>
      </Box>

      <ConfirmationDialog
        open={openDialog}
        message={t("confirmDelete")}
        workspaceName={selectedWorkspace?.name || ""}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />
    </Box>
  );
};

export default ViewWorkspaces;
